import React, {
  useEffect,
  useRef,
  useState,
  useCallback
} from 'react'
import { useMutation } from 'react-apollo'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import ControlStream from './control-stream'
import Image from './image'
import { loadScript } from '../../../lib/script-loader'
import styles from './autoplay-trailer.css'
import useStyles from './autoplay-trailer-stream.styles'
import { AUTOPLAY_TRAILER_PLAYER } from '../../../constants'
import PLAYBACK_AUTH from '../../../../graphql/mutations/playback-auth.gql'
import { getDRMSystem } from './utils/drm'
import { getOperatingSystem } from '../../../utils/os-device'

const PLAYER_ID = process.env.BC_PLAYER_ID
const ACCOUNT_ID = process.env.BC_ACCOUNT_ID
const scriptUrl = `https://players.brightcove.net/${ACCOUNT_ID}/${PLAYER_ID}_default/index.min.js`

/**
 * Trailer player wrapper used by multiple surfaces:
 * hotspot carousel, highlighted content, and title/series show page.
 *
 * This component supports both controlled and fallback mute modes:
 * - Controlled: parent passes `isMuted` and `onMuteChange` (hotspot carousel).
 * - Fallback: if `onMuteChange` is missing, mute state is kept locally
 *   so the volume control still works (highlighted content, title/series show page).
 *
 * It also coordinates Brightcove lifecycle (script + DRM + playback auth + player events)
 * and keeps UI state in sync with player playback state.
 */
const AutoplayTrailerStream = ({
  content,
  image,
  inHighlightedContent,
  isMuted,
  onMuteChange,
  onTrailerLoaded,
  onTrailerEnded
}) => {
  const videoElem = useRef(null)
  const onLoadedTimerRef = useRef(null)
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [player, setPlayer] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [shouldHideImage, setShouldHideImage] = useState(false)
  const [drmSystem, setDrmSystem] = useState(null)
  const [localIsMuted, setLocalIsMuted] = useState(isMuted)
  const classes = useStyles()
  const { name, version } = getOperatingSystem()
  const { id } = content
  // Single source used by UI and player:
  // - controlled value when parent provides onMuteChange
  // - local fallback otherwise
  const effectiveIsMuted = onMuteChange ? isMuted : localIsMuted

  // Load Script, drm
  useEffect(() => {
    (async () => {
      if (!scriptLoaded) {
        loadScript(scriptUrl, () => {
          setScriptLoaded(true)
        })
      }

      if (!drmSystem) {
        const drm = await getDRMSystem()
        setDrmSystem(drm)
      }
    })()
  }, [])

  // Get playback auth
  useEffect(() => {
    if (!drmSystem) {
      return
    }

    playbackAuth({
      variables: {
        drmLevel: drmSystem,
        os: name,
        osVersion: version,
        contentItemId: id
      }
    })
  }, [drmSystem])

  const [playbackAuth, { data: playbackAuthData }] = useMutation(PLAYBACK_AUTH)

  // Keep fallback state aligned if parent-controlled mute changes over time.
  useEffect(() => {
    if (onMuteChange) {
      setLocalIsMuted(isMuted)
    }
  }, [isMuted, onMuteChange])

  // Load player
  useEffect(() => {
    if (!scriptLoaded || !videoElem.current || !playbackAuthData) {
      return
    }

    setPlayer(window.bc(videoElem.current))
  }, [scriptLoaded, videoElem.current, playbackAuthData])

  // Load media and set event handlers
  useEffect(() => {
    if (!player || !playbackAuthData) {
      return undefined
    }
    const videoProcessed = player.catalog.transformVideoResponse(playbackAuthData.playAuth)
    player.catalog.load(videoProcessed)
    player.autoplay(false)

    player.on('play', onPlay)
    player.on('pause', onPause)
    player.on('loadeddata', onLoaded)
    player.on('ended', onEnded)

    return () => {
      player.off('play', onPlay)
      player.off('pause', onPause)
      player.off('loadeddata', onLoaded)
      player.off('ended', onEnded)
      player.dispose()
    }
  }, [player, playbackAuthData])

  // Event Handlers
  const onPlay = useCallback(() => {
    setIsPlaying(true)
    setShouldHideImage(true)
  }, [setIsPlaying, setShouldHideImage])

  const onPause = useCallback(() => {
    setIsPlaying(false)
  }, [setIsPlaying])

  const onEnded = useCallback(() => {
    setIsPlaying(false)
    setShouldHideImage(false)
    if (onTrailerEnded) {
      onTrailerEnded()
    }
  }, [
    setIsPlaying,
    setShouldHideImage,
    onTrailerEnded
  ])

  const onLoaded = useCallback(() => {
    let { delaySeconds } = content
    if (!delaySeconds) {
      delaySeconds = 0
    }
    // Notify parent when trailer is loaded so they can stop carousel autoplay
    if (onTrailerLoaded) {
      onTrailerLoaded()
    }

    onLoadedTimerRef.current = setTimeout(() => {
      if (!player) return
      player.muted(effectiveIsMuted)
      player.play()
    }, delaySeconds * 1000)
  }, [content, player, effectiveIsMuted, onTrailerLoaded])

  // Ensure player mute state tracks the current effective mute state after init and on updates.
  useEffect(() => {
    if (!player) return

    player.muted(effectiveIsMuted)
  }, [player, effectiveIsMuted])

  // Always update local fallback; notify parent when controlled mode is enabled.
  const handleMuteChange = useCallback((nextMuted) => {
    setLocalIsMuted(nextMuted)

    if (onMuteChange) {
      onMuteChange(nextMuted)
    }
  }, [onMuteChange])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (onLoadedTimerRef.current) {
        clearTimeout(onLoadedTimerRef.current)
      }
    }
  }, [])

  return (
    <div className={classnames(styles.wrapper, {
      [styles.inHighlightedContent]: inHighlightedContent
    })}
    >
      <div className={classnames(styles.videoWrapper, {
        [styles.inHighlightedContent]: inHighlightedContent
      })}
      >
        <ControlStream
          player={player}
          isPlaying={isPlaying}
          isMuted={effectiveIsMuted}
          onMuteChange={handleMuteChange}
          onInteraction={onTrailerLoaded}
          inHighlightedContent={inHighlightedContent}
        />

        <Image isHidden={shouldHideImage} image={image} />

        <div
          className={classnames(classes.video, {
            [classes.hidden]: !shouldHideImage
          })}
        >
          <div className={classes.videoRatio}>
            <div className={classes.videoContainer}>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                id={AUTOPLAY_TRAILER_PLAYER.id}
                className={classnames('video-js', classes.videoPlayer)}
                ref={videoElem}
              />
            </div>
          </div>
        </div>

        <div className={classnames(styles.videoLeftGradient, {
          [styles.inHighlightedContent]: inHighlightedContent
        })}
        />
        <div className={classnames(styles.videoBottomGradient, {
          [styles.inHighlightedContent]: inHighlightedContent
        })}
        />
      </div>
    </div>
  )
}

export const AutoplayTrailerContentType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  delaySeconds: PropTypes.number
})

AutoplayTrailerStream.propTypes = {
  content: AutoplayTrailerContentType.isRequired,
  image: PropTypes.string.isRequired,
  inHighlightedContent: PropTypes.bool,
  isMuted: PropTypes.bool,
  onMuteChange: PropTypes.func,
  onTrailerLoaded: PropTypes.func,
  onTrailerEnded: PropTypes.func
}

AutoplayTrailerStream.defaultProps = {
  inHighlightedContent: false,
  isMuted: true,
  onMuteChange: undefined,
  onTrailerLoaded: undefined,
  onTrailerEnded: undefined
}

export default AutoplayTrailerStream
